import React, { useEffect, useRef, useState } from 'react';
import * as htmlToImage from 'html-to-image';
import PortalLogo from '../PortalLogo';
import { shareImage, saveImage, isNative, shareToInstagramStory } from '../../utils/nativeShare';
import { API_BASE } from '../../utils/apiBase';
import cardFontCss from '../../utils/cardFonts';
import { RING } from '../wall/wallTokens';
import { XIcon } from '../portal/canvas/icons';

/**
 * Share your Portal (share/city/manage handoff §C). The live card IS the preview — the viewer
 * scales it down over the sheet — and what's rendered is what's on screen: the background, the
 * media, the words, the event and the venue, the author header, and a bottom band with ONLY ON
 * and the Portal lockup. Step 1 copies the link (Instagram drops links from shared pictures, so
 * it goes on a Link sticker); step 2 renders a 1080×1920 image, or a 6-second loop when
 * something on the Portal moves, and hands it to Instagram, a chat, or the photo roll.
 */
export const SHARE_HOST = 'heyportal.app';
export const shareUrl = (code) => `https://${SHARE_HOST}/p/${code}`;
export const shareLabel = (code) => `${SHARE_HOST}/p/${code}`;
/**
 * Every background that moves. The five WebGL ones are drawn from their canvas each frame; the
 * rest are CSS, and those are FILMED — see `filmLayer`. Either way a loop of one of these shows
 * what the card is doing, which is the only reason to offer a loop at all.
 */
const ANIMATED_BG = ['lasers', 'fractal', 'liquid', 'mirror', 'warp', 'stage', 'tunnel', 'aurora', 'signal', 'grid', 'fleck', 'haze'];
/** A Portal with no clip has nothing to run out of, so this is how long its loop runs. */
export const MAX_VIDEO_SECONDS = 10;
/** The composer won't cut a clip longer than this (StepTrim), so nothing should ask us to. */
export const MAX_CLIP_SECONDS = 30;
const FPS = 30;

/**
 * How long this Portal's loop runs — which is how long it holds the screen on Instagram, a
 * story being as long as the video in it.
 *
 * A clip runs its own length: it is the slice the sender kept (`trimStartMs`/`trimEndMs`), and
 * cutting it short loses the end while running past it only replays the same seconds. Anything
 * else — a shader, a moving style — could go forever, so it stops at ten.
 */
export function loopSeconds(media, el = null) {
  const trimmed = media && media.trimEndMs > media.trimStartMs ? (media.trimEndMs - media.trimStartMs) / 1000 : 0;
  const own = el && Number.isFinite(el.duration) && el.duration > 0 ? el.duration : 0;
  const clip = trimmed || own;
  if (!clip) return MAX_VIDEO_SECONDS;
  return Math.max(1, Math.min(MAX_CLIP_SECONDS, Math.round(clip)));
}

/** Is there anything here a recording can carry — a clip, or a background that moves? */
export function isAnimatedPortal(portal, media) {
  const l = portal?.layout || {};
  if (media?.kind === 'video') return true;
  return !media && ANIMATED_BG.includes(l.background || 'lasers');
}

/**
 * A background that moves but has no canvas of its own to read — so it has to be filmed.
 *
 * Asks the engine what is animating rather than trusting a list against an attribute: the list
 * was right and the DOM didn't say so (the CSS wrapper carried no `data-kind`), so nothing was
 * ever filmed and a loop of a moving background came out frozen. The attribute is there now and
 * is the fallback for anything without getAnimations.
 */
export function isFilmable(node) {
  const bg = node.querySelector('[data-testid="portal-background"]');
  if (!bg || bg.querySelector('canvas')) return null;          // a shader reads from its canvas
  try {
    if (bg.getAnimations?.({ subtree: true }).length) return bg;
  } catch { /* no getAnimations here; the list below still knows */ }
  return ANIMATED_BG.includes(bg.getAttribute('data-kind') || '') ? bg : null;
}

/**
 * Keep re-photographing a layer for as long as the loop runs.
 *
 * The moving backgrounds that aren't shaders are CSS — drifting gradients, a glow, some flecks —
 * and CSS lives in the DOM, where a video frame can't reach it. Redrawing the whole card thirty
 * times a second is out of the question, but this layer alone is a handful of divs with no text,
 * no pictures and no fonts to embed, and it is SOFT: gradients and blur. So it is filmed small
 * and often, and blown back up, which nobody can tell from the real thing — where the old code
 * simply froze it on frame one.
 *
 * Whatever rate the device manages is the rate the background moves at; the shader, the clip and
 * the portal in the lockup are unaffected and stay at thirty.
 */
export function filmLayer(el, width) {
  const w = Math.max(120, Math.round(width));
  const h = Math.round((w * el.clientHeight) / (el.clientWidth || 1));
  let stopped = false;
  let latest = null;
  const shoot = async () => {
    const blob = await htmlToImage.toBlob(el, {
      width: w,
      height: h,
      pixelRatio: 1,
      skipFonts: true,                                          // there are none in here
      style: {
        width: `${el.clientWidth}px`,
        height: `${el.clientHeight}px`,
        transform: `scale(${w / (el.clientWidth || 1)})`,
        transformOrigin: 'top left',
      },
    });
    if (!blob) throw new Error('nothing to film');
    const url = URL.createObjectURL(blob);
    const img = await loadImage(url);
    const old = latest;
    latest = img;
    if (old) URL.revokeObjectURL(old.src);
    return img;
  };
  const roll = async () => {
    while (!stopped) {
      try { await shoot(); } catch { /* keep the frame we have */ }
      // A breather between takes. One costs 20-40ms on a phone, so this still films the
      // background at better than fifteen a second while leaving the recorder its own air.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => { setTimeout(r, 30); });
    }
  };
  return {
    first: () => shoot(),                 // one in hand before the recorder starts
    start: () => { roll(); },
    get: () => latest,
    stop: () => {
      stopped = true;
      if (latest) URL.revokeObjectURL(latest.src);
    },
  };
}

/** Seek the clip to `sec` and play, resolving once the seek has landed (or soon enough). */
export function restartClip(clip, sec, { timeoutMs = 800 } = {}) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      clip.removeEventListener('seeked', finish);
      Promise.resolve(clip.play?.()).catch(() => {});
      resolve();
    };
    const timer = setTimeout(finish, timeoutMs);
    // Already there: no seek will happen and no `seeked` will come. Decided BEFORE the
    // assignment — `currentTime` reads back the new value the moment it is set, long before
    // the frame is, so checking afterwards would skip the wait every time.
    const already = Math.abs((clip.currentTime || 0) - sec) < 0.05 && clip.readyState >= 2;
    clip.addEventListener('seeked', finish);
    if (already) { finish(); return; }
    try { clip.currentTime = sec; } catch { finish(); }
  });
}

/** mp4 where the engine records it (iOS Safari, Chrome 126+), else webm. */
export function pickVideoMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  return ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm'].find((t) => { try { return MediaRecorder.isTypeSupported(t); } catch { return false; } }) || null;
}

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

/** The band at the foot of the card while sharing: the sticker hint (preview only) and ONLY ON. */
export function ShareBand({ hint = true, still = false }) {
  return (
    <div aria-hidden data-testid="share-band" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 3, height: '22%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 14, paddingBottom: 28, background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,.85))', pointerEvents: 'none' }}>
      {hint && (
        <span data-share-skip className="font-mono" style={{ height: 56, padding: '0 26px', display: 'flex', alignItems: 'center', border: '2px dashed rgba(255,255,255,.55)', borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.8)' }}>LINK STICKER GOES HERE</span>
      )}
      <span className="font-mono" style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#fff' }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em' }}>ONLY ON</span>
        <PortalLogo cap={26} still={still} />
      </span>
    </div>
  );
}

const CAPTURE_SKIP = (node) => !(node?.matches?.('[data-share-skip]'));

/** A 1×1 nothing. Stands in for a picture we couldn't fetch, so one bad URL costs a face, not the share. */
const TRANSPARENT_PX = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
const MEDIA_TIMEOUT_MS = 12000;
const isRemote = (src) => !!src && /^https?:\/\//i.test(src) && !src.startsWith(`${window.location.origin}/`);

/** Resolves when the element has pixels to draw — or when we've waited long enough to stop waiting. */
function settled(el) {
  const ready = () => (el.tagName === 'VIDEO' ? el.readyState >= 2 : el.complete && el.naturalWidth > 0);
  if (ready()) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => { clearTimeout(timer); el.removeEventListener('loadeddata', done); el.removeEventListener('load', done); el.removeEventListener('error', done); resolve(); };
    const timer = setTimeout(done, MEDIA_TIMEOUT_MS);
    el.addEventListener('loadeddata', done);
    el.addEventListener('load', done);
    el.addEventListener('error', done);
  });
}

/** The same image proxy the event ShareSheet's canvas uses — a same-origin copy of a remote file. */
async function proxied(src) {
  const res = await fetch(`${API_BASE}/events/proxy-image?url=${encodeURIComponent(src)}`);
  if (!res.ok) throw new Error(`proxy ${res.status}`);
  return res.blob();
}

/** Pictures go in as `data:` — html-to-image leaves those alone instead of re-fetching them
 *  with a cache-busting query string, which a `blob:` URL has no way to answer. */
function asDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('unreadable'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}

/**
 * The same picture, no bigger than it is drawn.
 *
 * The author's face is a 30px circle on the card and an 800×536 file on the CDN: inlined whole
 * it was 810KB of base64 inside the SVG, most of a payload a phone then declined to finish
 * decoding. Three times its drawn size is plenty for a 1080-wide frame.
 */
async function shrunk(blob, drawnWidth) {
  const cap = Math.max(96, Math.round((drawnWidth || 0) * 3));
  try {
    const bmp = await createImageBitmap(blob);
    if (bmp.width <= cap) { bmp.close?.(); return asDataUrl(blob); }
    const h = Math.round((bmp.height * cap) / bmp.width);
    const cv = document.createElement('canvas');
    cv.width = cap; cv.height = h;
    const ctx = cv.getContext('2d');
    if (!ctx) { bmp.close?.(); return asDataUrl(blob); }
    ctx.drawImage(bmp, 0, 0, cap, h);
    bmp.close?.();
    const small = await new Promise((res) => cv.toBlob(res, 'image/webp', 0.9));
    return asDataUrl(small && small.size < blob.size ? small : blob);
  } catch {
    return asDataUrl(blob);   // no createImageBitmap, or a format it won't take
  }
}

/**
 * Swap every remote picture and clip on the card for a same-origin copy, and hand back the undo.
 *
 * The Spaces CDN answers without CORS headers. html-to-image fetches each image to inline it,
 * that fetch fails, it substitutes an empty src, and the clone's `<img>` fires `error` — which
 * it turns into a rejection carrying an Event, not an Error. So every card with a face on it
 * (which is every card: the author header) died mid-render with no message to show for it. A
 * clip is worse than that: drawing a cross-origin `<video>` taints the canvas, and a tainted
 * canvas can be neither read as a PNG nor recorded.
 *
 * Anything we can't proxy is left alone — `imagePlaceholder` keeps the render alive without it.
 */
export async function inlineRemoteMedia(node) {
  const undo = [];
  const urls = [];
  const els = [...node.querySelectorAll('img, video')].filter((el) => {
    const src = el.currentSrc || el.src;
    if (!src || src.startsWith('data:')) return false;
    // A native HLS clip (Android Chrome, Safari) is a manifest, not a file: the proxy can't
    // hand back anything a <video> could play. StoryMedia loads it with crossOrigin instead,
    // which is what keeps its frames drawable. Leave it exactly as it is.
    if (/\.m3u8(\?|$)/i.test(src)) return false;
    // Remote ones have to come through the proxy. OURS have to be inlined too, and that is not
    // about CORS at all: WebKit paints html-to-image's SVG before the images nested inside it
    // have decoded, so on the one capture we take they are simply missing — which is where the
    // O in ONLY ON PORTAL went. An <img> already carrying a decoded data: URL is there from the
    // start. Measured on iOS 18: nothing of it in the first pass, all of it once pre-inlined.
    return el.tagName === 'IMG' || isRemote(src);
  });

  await Promise.all(els.map(async (el) => {
    const original = el.getAttribute('src');
    const isVideo = el.tagName === 'VIDEO';
    let swapped;
    const src = el.currentSrc || el.src;
    try {
      const copy = isRemote(src) ? await proxied(src) : await fetch(src).then((r) => r.blob());
      // A clip stays a blob: nothing fetches it — only drawImage reads it, frame by frame.
      swapped = isVideo ? URL.createObjectURL(copy) : await shrunk(copy, el.clientWidth);
      if (isVideo) urls.push(swapped);
    } catch {
      return; // the picture stays remote; the capture survives it
    }
    if (isVideo) {
      const at = el.currentTime;
      const wasPlaying = !el.paused;
      const resume = () => { try { el.currentTime = at; } catch { /* not seekable yet */ } if (wasPlaying) el.play().catch(() => {}); };
      undo.push(() => { el.src = original; el.load(); resume(); });
      el.src = swapped;
      await settled(el);
      resume();
    } else {
      const srcset = el.getAttribute('srcset');
      undo.push(() => { el.src = original; if (srcset) el.setAttribute('srcset', srcset); });
      el.removeAttribute('srcset');
      el.src = swapped;
      await settled(el);
      // Loaded is not decoded, and it is the decode the rasterizer waits on.
      try { await el.decode?.(); } catch { /* it will have to take its chances */ }
    }
  }));

  return () => {
    undo.forEach((f) => { try { f(); } catch { /* the card is on its way out anyway */ } });
    urls.forEach((u) => URL.revokeObjectURL(u));
  };
}

/**
 * A share the browser refused. The gesture rule is the one worth translating: it means the tap
 * went stale while we rendered, and the answer is simply to tap the (now armed) button again.
 */
export function deliverMessage(e) {
  const m = e?.message || '';
  if (e?.name === 'NotAllowedError' || /user gesture|user activation/i.test(m)) return 'Tap again to open the share sheet';
  return m || 'That didn’t work';
}

/** html-to-image rejects with the `error` Event when an image won't load — an Error it is not. */
export function captureMessage(e) {
  if (e?.message) return e.message;
  if (e?.target?.tagName === 'IMG') return 'A picture on the card wouldn’t load';
  return 'That didn’t work';
}

/** The story frame every Portal is delivered in. */
const FRAME_W = 1080;
const FRAME_H = 1920;
/** One turn of the portal, matching .pr-portal-spin in index.css. */
const SPIN_MS = 10000;

/**
 * Where the card lands inside the frame: the WHOLE card, centred, nothing cut.
 *
 * It used to be cropped to the top 16:9 of the card, which is fine on a 16:9 card and wrong on
 * every phone made this decade — a 20:9 screen lost its bottom fifth, taking ONLY ON PORTAL
 * with it and leaving everything else sitting lower in the frame than the preview promised.
 */
export function fitCard(w, h) {
  const k = Math.min(FRAME_W / w, FRAME_H / h);
  const dw = Math.round(w * k);
  const dh = Math.round(h * k);
  return { k, dw, dh, dx: Math.round((FRAME_W - dw) / 2), dy: Math.round((FRAME_H - dh) / 2) };
}

/** The layer whose pixels we draw ourselves, rather than asking html-to-image to carry them. */
export function moverOf(node) {
  return node.querySelector('[data-testid="portal-shader"] canvas')
    || node.querySelector('[data-testid="portal-media-layer"] video')
    || node.querySelector('[data-testid="portal-media-layer"] img');
}

/**
 * Where `el` sits inside `root`, in the root's own coordinates.
 *
 * Measured from client rects and divided by whatever scale the root is under (the card is at
 * half size behind the sheet), because the obvious way — walking offsetParent — is not the same
 * walk in every engine: WebKit breaks the chain differently, and the portal in ONLY ON PORTAL
 * came out drawn somewhere off the card, leaving a hole where the O should be.
 *
 * Null when the box is empty or lands outside the root, so the caller can leave it alone.
 */
function boxIn(el, root) {
  if (!el || !root) return null;
  const a = root.getBoundingClientRect();
  const b = el.getBoundingClientRect();
  const scale = a.width / (root.clientWidth || a.width || 1);
  if (!scale || !Number.isFinite(scale) || !b.width || !b.height) return null;
  const box = { x: (b.left - a.left) / scale, y: (b.top - a.top) / scale, w: b.width / scale, h: b.height / scale };
  const w = root.clientWidth || a.width / scale;
  const h = root.clientHeight || a.height / scale;
  if (box.x < -box.w || box.y < -box.h || box.x > w || box.y > h) return null;
  return box;
}

/** Fill `box` with `src`, cropping the overflow — the card's own object-fit: cover. */
function drawCover(ctx, src, box) {
  const sw = src.videoWidth || src.naturalWidth || src.width;
  const sh = src.videoHeight || src.naturalHeight || src.height;
  if (!sw || !sh) return;
  const s = Math.max(box.dw / sw, box.dh / sh);
  const w = sw * s; const h = sh * s;
  ctx.save();
  ctx.beginPath(); ctx.rect(box.dx, box.dy, box.dw, box.dh); ctx.clip();
  try { ctx.drawImage(src, box.dx + (box.dw - w) / 2, box.dy + (box.dh - h) / 2, w, h); } catch { /* a frame we can't read stays black */ }
  ctx.restore();
}

const loadImage = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

/**
 * The card's own black backdrops, out of the way for the length of the capture.
 *
 * The overlay is drawn OVER the moving layer, so anything opaque behind the words would hide
 * it. The frame is filled with black first, so nothing looks different for it.
 */
function clearBackdrops(node) {
  const undo = [];
  node.querySelectorAll('[data-testid="portal-canvas"]').forEach((el) => {
    undo.push([el, el.style.background]);
    el.style.background = 'transparent';
  });
  return () => undo.forEach(([el, was]) => { el.style.background = was; });
}

/**
 * Everything except the moving layer, as a transparent PNG the size of the card in the frame.
 *
 * `skip` drops the layer we draw ourselves. Asking html-to-image for it meant a 267KB PNG of
 * the WebGL canvas inside the SVG — which a phone is free to decode or not, and sometimes did
 * not, handing back a black card. Drawn straight onto the frame it is simply always there.
 */
async function captureOverlay(node, fit, skip) {
  const w = node.clientWidth || 400;
  const h = node.clientHeight || Math.round((w * 16) / 9);
  const fontEmbedCSS = await cardFontCss(node).catch(() => '');
  return htmlToImage.toBlob(node, {
    width: fit.dw,
    height: fit.dh,
    pixelRatio: 1,
    imagePlaceholder: TRANSPARENT_PX,
    ...(fontEmbedCSS ? { fontEmbedCSS } : {}),
    style: {
      width: `${w}px`,
      height: `${h}px`,
      transform: `scale(${fit.k})`,
      transformOrigin: 'top left',
      borderRadius: '0',
      background: 'transparent',
    },
    filter: (n) => CAPTURE_SKIP(n) && !skip.includes(n),
  });
}

/** The still: black, the moving layer, then everything else on top. */
async function composeStill(node, { spinning = false, onDomRead = null, film = null } = {}) {
  const w = node.clientWidth || 400;
  const h = node.clientHeight || Math.round((w * 16) / 9);
  const fit = fitCard(w, h);
  const mover = moverOf(node);
  // The portal in ONLY ON PORTAL turns on its own; in a loop we draw it, so it stays out of the
  // overlay — but only if we know where to put it. Somewhere we can't measure, it stays in the
  // overlay and sits still: a portal that doesn't turn beats a hole where the O should be.
  const ringEl = spinning ? node.querySelector('img[data-portal-o]') : null;
  const ringBox = boxIn(ringEl, node);
  const ring = ringBox ? ringEl : null;
  // Skip from the BACKGROUND down, not just the shader: its wrapper is an opaque black square,
  // and leaving that in the overlay paints right over the layer we just drew. A filmed CSS
  // background comes out for the same reason — it is supplied frame by frame instead.
  const skip = [mover?.closest('[data-testid="portal-background"], [data-testid="portal-media-layer"]'), ring, film?.el].filter(Boolean);

  // The card is only held still while it is being READ — about a second — not for the whole
  // take. A ten-second loop is nine more seconds of drawing canvases, and the card carries on.
  const restore = clearBackdrops(node);
  onDomRead?.(true);
  let overlayBlob;
  try { overlayBlob = await captureOverlay(node, fit, skip); } finally { restore(); onDomRead?.(false); }
  if (!overlayBlob) throw new Error('Nothing came out of the render.');
  const overlay = await loadImage(URL.createObjectURL(overlayBlob));

  const canvas = document.createElement('canvas');
  canvas.width = FRAME_W; canvas.height = FRAME_H;
  const ctx = canvas.getContext('2d');
  const paint = (turn = 0) => {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, FRAME_W, FRAME_H);
    if (mover) drawCover(ctx, mover, fit);
    const frame = film?.reel.get();
    if (frame) ctx.drawImage(frame, fit.dx, fit.dy, fit.dw, fit.dh);
    ctx.drawImage(overlay, fit.dx, fit.dy, fit.dw, fit.dh);
    if (ring && ringBox) {
      const rw = ringBox.w * fit.k; const rh = ringBox.h * fit.k;
      const cx = fit.dx + (ringBox.x + ringBox.w / 2) * fit.k;
      const cy = fit.dy + (ringBox.y + ringBox.h / 2) * fit.k;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(turn * 2 * Math.PI);
      try { ctx.drawImage(ring, -rw / 2, -rh / 2, rw, rh); } catch { /* leave the O empty */ }
      ctx.restore();
    }
  };
  paint();
  return { canvas, ctx, paint, overlay, fit };
}

/** The card as a 1080×1920 PNG blob. */
export async function renderCardImage(node, { onDomRead = null } = {}) {
  const { canvas, overlay } = await composeStill(node, { onDomRead });
  URL.revokeObjectURL(overlay.src);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

/**
 * A 6-second loop: the moving layer redrawn every frame under a still capture of everything
 * else, with the portal in the lockup turning at its own 10-second pace.
 */
export async function renderCardVideo(node, { onProgress = null, seconds = MAX_VIDEO_SECONDS, startAtMs = 0, onDomRead = null, stopped = null } = {}) {
  const mime = pickVideoMime();
  if (!mime) throw new Error('This browser can’t record video.');
  // A CSS background can't be read from a canvas, so it is filmed alongside the recording.
  const filmEl = isFilmable(node);
  const film = filmEl ? { el: filmEl, reel: filmLayer(filmEl, 480) } : null;
  if (film) { try { await film.reel.first(); } catch { /* the first frames go without it */ } }
  const { canvas, paint, overlay } = await composeStill(node, { spinning: true, onDomRead, film });
  film?.reel.start();
  // The card is held while the sheet is open, which pauses a clip — so a clip Portal recorded
  // its first frame twelve times over. Run it for the length of the take and put it back after;
  // StoryMedia's own listener keeps it inside the trim window.
  const clip = node.querySelector('[data-testid="portal-media-layer"] video');
  const wasAt = clip?.currentTime;
  if (clip) {
    // From the top of the window, and not a frame before the seek has landed: on a native
    // HLS player the first frames after `currentTime =` are still the old position, so a
    // loop started at once opened on the end of the clip rather than its start.
    await restartClip(clip, startAtMs / 1000);
  }
  const stream = canvas.captureStream(FPS);
  // Ask for each frame by hand as well as declaring a rate. A canvas that is never painted to
  // the screen — and this one only ever exists to be recorded — is not reliably ticked by
  // WebKit on its own, which is how a ten-second loop came back as a single frame: a video
  // that is a photo. requestFrame is the explicit way and both engines coalesce the extras.
  const [track] = stream.getVideoTracks?.() || [];
  const tick = () => { try { track?.requestFrame?.(); } catch { /* auto-capture only, then */ } };
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 6_000_000 });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
  const done = new Promise((res) => { rec.onstop = () => res(new Blob(chunks, { type: mime.split(';')[0] })); });
  rec.start(200);
  paint(0); tick();                    // a first frame in hand before the clock starts
  const frames = seconds * FPS;
  const t0 = performance.now();
  for (let f = 0; f < frames; f += 1) {
    if (stopped?.()) break;   // the sheet was closed: stop drawing into a file nobody will take
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, Math.max(0, t0 + (f * 1000) / FPS - performance.now())));
    paint((((f * 1000) / FPS) % SPIN_MS) / SPIN_MS);
    tick();
    onProgress?.(Math.round(((f + 1) / frames) * 100));
  }
  rec.stop();
  const blob = await done;
  film?.reel.stop();
  URL.revokeObjectURL(overlay.src);
  if (clip) {
    clip.pause?.();
    try { clip.currentTime = wasAt ?? 0; } catch { /* not seekable yet */ }
  }
  return { blob, mime: blob.type };
}

export default function PortalShareSheet({ stageRef, portal, media = null, animated = false, onCapturing = null, onClose, copiedAtStart = false, onCopied = null, reduced = false }) {
  const [copied, setCopied] = useState(copiedAtStart);
  // A browser with no MediaRecorder can't be offered a loop — least of all silently, now that
  // the draw starts on its own and the failure would greet you before you'd asked for anything.
  const canRecord = useRef(!!pickVideoMime()).current;
  const [fmt, setFmt] = useState(animated && canRecord ? 'video' : 'image');
  const [phase, setPhase] = useState(null);      // null | 'prep' | 'render' | 'armed' | 'done'
  const [pct, setPct] = useState(0);
  const [kind, setKind] = useState(null);        // 'ig' | 'msg' | 'save'
  const [nudge, setNudge] = useState(false);
  const [error, setError] = useState('');
  // What we've already rendered, waiting for a tap to carry it out. See `run`.
  const [ready, setReady] = useState(null);      // { fmt, blob, fileName }
  const jobRef = useRef(null);                   // { fmt, promise } — the draw in flight
  const alive = useRef(true);
  // Set on the way in as well as cleared on the way out: StrictMode mounts, unmounts and
  // mounts again, and a flag that is only ever cleared stays cleared — every state update
  // after a render then went nowhere, which in dev is the whole flow below.
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // A picture is not a ten-second loop: what's in hand no longer answers the question.
  useEffect(() => { setReady(null); setPhase(null); setError(''); jobRef.current = null; }, [fmt]);

  const code = portal?.inviteCode || '';
  const url = shareUrl(code);

  // An untrimmed clip only says how long it is once its metadata is in, so ask again then —
  // the button has to promise the length the loop will actually be.
  const [secs, setSecs] = useState(() => loopSeconds(media));
  useEffect(() => {
    const clip = stageRef?.current?.querySelector('[data-testid="portal-media-layer"] video');
    setSecs(loopSeconds(media, clip));
    if (!clip || Number.isFinite(clip.duration)) return undefined;
    const onMeta = () => setSecs(loopSeconds(media, clip));
    clip.addEventListener('loadedmetadata', onMeta);
    return () => clip.removeEventListener('loadedmetadata', onMeta);
  }, [media, stageRef]);

  const copy = async () => {
    const ok = await copyText(url);
    if (ok) { setCopied(true); setNudge(false); setError(''); onCopied?.(); } else setError('COULDN’T COPY · LONG-PRESS THE LINK TO COPY IT');
  };

  /** Hand the file over. Must run INSIDE the tap — see `run`. */
  const deliver = async (which, out) => {
    setKind(which); setError(''); setNudge(false);
    try {
      if (which === 'save') {
        await saveImage({ blob: out.blob, fileName: out.fileName });
      } else if (which === 'ig' && await shareToInstagramStory({ blob: out.blob, video: fmt === 'video' })) {
        // Straight into the story composer in the native shells. Everywhere else — and for a
        // DM, which is what the message button is for — it's the OS sheet below.
      } else {
        // No text and no url alongside the file: Instagram drops the picture when a link rides with it.
        await shareImage({ blob: out.blob, fileName: out.fileName, title: 'Portal' });
      }
      if (alive.current) setPhase('done');
    } catch (e) {
      // Still in hand — the next tap can try again with a fresh gesture.
      if (alive.current) { setPhase('armed'); setError(deliverMessage(e).toUpperCase()); }
    }
  };

  const draw = async (node) => {
    setPct(0); setError('');
    // Every remote picture and clip becomes a same-origin copy for the length of the capture,
    // then goes back to what it was — see inlineRemoteMedia.
    const restoreMedia = await inlineRemoteMedia(node);
    const onDomRead = (on) => onCapturing?.(on);
    try {
      let blob; let fileName;
      if (fmt === 'video') {
        const out = await renderCardVideo(node, {
          seconds: secs,
          startAtMs: media?.trimStartMs || 0,
          onDomRead,
          stopped: () => !alive.current,
          onProgress: (p) => alive.current && setPct(p),
        });
        blob = out.blob;
        fileName = `portal-${code}.${blob.type.includes('mp4') ? 'mp4' : 'webm'}`;
      } else {
        setPct(30);
        blob = await renderCardImage(node, { onDomRead });
        fileName = `portal-${code}.png`;
        setPct(100);
      }
      if (!blob) throw new Error('Nothing came out of the render.');
      return { fmt, blob, fileName };
    } finally {
      restoreMedia();
    }
  };

  /**
   * One render per format, started the moment the sheet opens and joined by whoever taps.
   *
   * A ten-second loop takes ten seconds to record — so it starts while you are still reading
   * the line about Link stickers, and by the time you reach INSTAGRAM STORY it is either done
   * (straight out, one tap) or halfway, and the bar picks up where it got to instead of at nought.
   */
  const startDraw = () => {
    const node = stageRef?.current;
    if (!node) return null;
    if (jobRef.current?.fmt === fmt) return jobRef.current.promise;
    const promise = draw(node).then(
      (out) => { if (alive.current) setReady(out); return out; },
      // Quiet on its own: whoever is waiting says so. A draw nobody asked for must not put an
      // error on screen, and a failed one is thrown away so the next tap can try again.
      (e) => { jobRef.current = null; throw e; },
    );
    jobRef.current = { fmt, promise };
    promise.catch(() => {});
    return promise;
  };

  // Off it goes: on open, and again whenever the format changes under it.
  useEffect(() => {
    if (!stageRef?.current) return undefined;
    setPhase('prep');
    const settle = () => { if (alive.current) setPhase((p) => (p === 'prep' ? null : p)); };
    startDraw()?.then(settle, settle);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fmt, stageRef]);

  /**
   * Two things have to happen and the browser will not let them happen together: the card has
   * to be drawn, and the share sheet has to be opened. `navigator.share` may only be called
   * while the tap that asked for it is still live, and rendering a 6-second loop outlives that
   * by a mile — which is exactly what "must be handling a user gesture to perform a share
   * request" means. So the render happens on the first tap and is KEPT; the tap that follows
   * finds the file already in hand and goes straight out, still inside its own gesture.
   *
   * The native shell has no such rule (the OS sheet is a plugin call, not a web one), so there
   * it stays one tap. Either way the file is only rendered once per format.
   */
  const run = async (which) => {
    if (!copied) { setNudge(true); return; }
    // Already drawn: this call is still inside the tap, which is the whole point.
    if (ready?.fmt === fmt) { deliver(which, ready); return; }

    const job = startDraw();          // joins the one already running, at whatever % it's reached
    if (!job) return;
    setKind(which); setPhase('render'); setNudge(false);
    try {
      const out = await job;
      if (!alive.current) return;
      if (isNative()) { deliver(which, out); return; }
      setPhase('armed');
    } catch (e) {
      if (alive.current) { setPhase(null); setError(captureMessage(e).toUpperCase()); }
    }
  };

  const doneText = {
    ig: 'OPENED INSTAGRAM · ADD THE LINK STICKER, PASTE, POST',
    msg: 'OPENED YOUR SHARE SHEET · PICK A CHAT',
    save: fmt === 'video' ? 'SAVED THE VIDEO TO YOUR PHOTOS' : 'SAVED THE IMAGE TO YOUR PHOTOS',
  }[kind];
  // Rendered and waiting: the browser only opens a share sheet from a live tap, so it needs one.
  const armedText = {
    ig: 'READY · TAP INSTAGRAM STORY TO SEND IT',
    msg: 'READY · TAP THE MESSAGE BUTTON TO SEND IT',
    save: 'READY · TAP SAVE AGAIN',
  }[kind] || 'READY · TAP AGAIN';
  // What the line under the buttons says, in order of what matters most right now.
  const spec = fmt === 'video' ? `1080×1920 · ${secs}S LOOP` : '1080×1920';
  const resting = ready?.fmt === fmt ? `${spec} · READY` : `${spec} · WHAT YOU SEE ABOVE`;
  const note = error
    || (phase === 'done' ? doneText
      : phase === 'armed' ? armedText
        : nudge ? 'COPY THE LINK FIRST, SO YOU CAN PASTE IT IN YOUR STORY'
          : phase === 'prep' ? `${spec} · GETTING IT READY ${pct}%`
            : resting);
  const noteColor = error || nudge ? '#FF33C6' : phase === 'done' || phase === 'armed' || (!phase && ready?.fmt === fmt) ? '#00FFFF' : 'rgba(255,255,255,.5)';
  const circle = { height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: 'rgba(255,255,255,.12)', color: '#fff', border: 0, borderRadius: '50%', cursor: 'pointer' };

  return (
    <>
      <div className="font-mono text-white" data-testid="share-header" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(14px + env(safe-area-inset-top, 0px))', zIndex: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em' }}>SHARE YOUR PORTAL</span>
        <button type="button" onClick={onClose} aria-label="Close" className="prc-pill" style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: 'rgba(255,255,255,.14)', color: '#fff', border: 0, borderRadius: '50%', cursor: 'pointer' }}><XIcon size={12} /></button>
      </div>
      <div role="dialog" aria-label="Share your Portal" data-testid="share-sheet" className="font-mono text-white" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 21, background: '#0B0B0B', padding: '14px 16px calc(16px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box', display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gridAutoRows: 'max-content', gap: 10, animation: reduced ? 'none' : 'prcSlideUp .44s cubic-bezier(.2,.7,.2,1) both' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span data-testid="share-step1" style={{ width: 20, height: 20, flexShrink: 0, borderRadius: '50%', background: copied ? '#00FFFF' : '#fff', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{copied ? '✓' : '1'}</span>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em' }}>COPY YOUR LINK FIRST</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px 4px 16px', background: 'rgba(255,255,255,.07)', borderRadius: 999 }}>
          <span data-testid="share-link" style={{ flex: 1, minWidth: 0, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'text', WebkitUserSelect: 'text' }}>{shareLabel(code)}</span>
          <button type="button" onClick={copy} data-testid="copy-link" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', background: copied ? '#00FFFF' : '#fff', color: '#000', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer', transition: 'background-color .2s' }}>{copied ? 'COPIED' : 'COPY LINK'}</button>
        </div>
        <span style={{ fontSize: 10, lineHeight: 1.45, color: 'rgba(255,255,255,.6)' }}>Instagram drops links from shared pictures. When your story opens, add a Link sticker and paste it.</span>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 20, height: 20, flexShrink: 0, borderRadius: '50%', background: copied ? '#fff' : 'rgba(255,255,255,.2)', color: copied ? '#000' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>2</span>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em' }}>SHARE IT AS</span>
          </span>
          <div style={{ display: 'flex', gap: 4, padding: 3, background: 'rgba(255,255,255,.1)', borderRadius: 999 }} data-testid="share-format">
            {[['image', 'IMAGE', false, 'A still 1080×1920 picture'], ['video', `VIDEO · ${secs}S`, !animated || !canRecord, !canRecord ? 'This browser can’t record video' : animated ? `A ${secs}-second loop of the moving parts` : 'Nothing on this Portal moves']].map(([k, label, off, title]) => (
              <button key={k} type="button" disabled={off} title={title} aria-pressed={fmt === k} onClick={() => { setFmt(k); setPhase(null); }} style={{ height: 28, padding: '0 12px', background: fmt === k ? '#fff' : 'transparent', color: fmt === k ? '#000' : '#fff', opacity: off ? 0.35 : 1, border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', cursor: off ? 'default' : 'pointer', transition: 'background-color .2s, color .2s' }}>{label}</button>
            ))}
          </div>
        </div>

        {phase === 'render' ? (
          <div data-testid="share-progress" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px', background: 'rgba(255,255,255,.07)', borderRadius: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em' }}><span>{fmt === 'video' ? `RENDERING A ${secs}-SECOND VIDEO…` : 'RENDERING YOUR IMAGE…'}</span><span style={{ color: '#00FFFF' }}>{pct}%</span></div>
            <div style={{ height: 4, borderRadius: 999, background: 'rgba(255,255,255,.14)', overflow: 'hidden' }}><div style={{ height: 4, width: `${pct}%`, background: '#00FFFF', borderRadius: 999 }} /></div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 52px 52px', gap: 6, opacity: copied ? 1 : 0.4, transition: 'opacity .2s' }} data-testid="share-actions">
            <button type="button" onClick={() => run('ig')} style={{ height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: `linear-gradient(#000,#000) padding-box, ${RING} border-box`, border: '2px solid transparent', color: '#fff', borderRadius: 999, fontFamily: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg>
              INSTAGRAM STORY
            </button>
            <button type="button" onClick={() => run('msg')} aria-label="Send in a message" title="Message" className="prc-pill" style={circle}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><path d="M4 4h16v12H9l-5 4z" /></svg></button>
            <button type="button" onClick={() => run('save')} aria-label="Save to photos" title="Save" className="prc-pill" style={circle}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><path d="M12 4v11M7 10l5 5 5-5" /><path d="M4 20h16" /></svg></button>
          </div>
        )}
        <span data-testid="share-note" style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: noteColor }}>{note}</span>
      </div>
    </>
  );
}
